import { journeyPresentation } from '../components/TransactionJourney';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout, { Product as D } from '../components/DashboardLayout';
import { Card, EmptyState, ErrorState, PageHeader, PrimaryButton, SecondaryButton, StatusPill, customerUiGlobalCss } from '../components/customerUi';
import { getPendingPaymentExpiry, resolveEscrowUiState } from '../components/transactionState';
import api from '../utils/api';
import { trackStartTransaction } from '../utils/analytics';
import { ArrowRight, FileText, Plus, Search } from 'lucide-react';

function fmt(value) {
  const n = Number(value);
  if (value === null || value === undefined || isNaN(n)) return 'R0.00';
  return `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function TransactionsList() {
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { filterTransactions(); }, [transactions, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [userRes, transactionsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/transactions'),
      ]);
      setUser(userRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
      else setLoadError('We could not load your transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    if (!searchTerm) { setFilteredTransactions(transactions); return; }
    const term = searchTerm.toLowerCase();
    setFilteredTransactions(transactions.filter(t =>
      t.buyer_name.toLowerCase().includes(term) ||
      t.seller_name.toLowerCase().includes(term) ||
      t.item_description.toLowerCase().includes(term) ||
      t.transaction_id.toLowerCase().includes(term)
    ));
  };

  const goToTransaction = (t) => navigate(
    t.deal_type && String(t.deal_type).startsWith('DIGITAL_WORK')
      ? `/smart-deals/${t.deal_id || t.transaction_id}`
      : `/transactions/${t.transaction_id}`
  );

  if (loading) {
    return (
      <DashboardLayout user={null} loading darkMain>
        <div />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user} darkMain>
      <style>{customerUiGlobalCss}</style>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <PageHeader
          title="Your transactions"
          subtitle={`${filteredTransactions.length} record${filteredTransactions.length !== 1 ? 's' : ''}`}
          action={
            <PrimaryButton
              data-testid="new-transaction-btn"
              onClick={() => { trackStartTransaction({ source: 'transactions_list_header' }); navigate('/transactions/new'); }}
            >
              <Plus size={15} /> New transaction
            </PrimaryButton>
          }
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: D.hover, border: `1px solid ${D.border}`, borderRadius: D.radiusSm, padding: '10px 14px' }}>
          <Search size={15} color={D.sub} />
          <input
            type="text"
            aria-label="Search your transactions" placeholder="Search by item, buyer or seller…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            data-testid="search-transactions-input"
            style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: D.text, fontFamily: D.sans, fontSize: 14 }}
          />
        </div>

        <Card style={{ padding: filteredTransactions.length === 0 ? 20 : '8px 10px' }}>
          {loadError ? <ErrorState title="Transactions unavailable" body={loadError} action={<SecondaryButton onClick={fetchData}>Try again</SecondaryButton>} /> : filteredTransactions.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={searchTerm ? 'No transactions match your search' : 'No transactions yet'}
              body={searchTerm ? 'Try an item description or participant name.' : 'Agree a deal, invite the other party and follow the payment and delivery steps here.'}
              action={!searchTerm && (
                <PrimaryButton
                  data-testid="empty-state-create-transaction"
                  onClick={() => { trackStartTransaction({ source: 'transactions_list_empty_state' }); navigate('/transactions/new'); }}
                >
                  <Plus size={15} /> Create your first transaction
                </PrimaryButton>
              )}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredTransactions.map((t) => {
                const uiState = resolveEscrowUiState(t);
                const journey = journeyPresentation(t, uiState);
                const counterparty = t.buyer_user_id === user?.user_id || t.buyer_email === user?.email ? t.seller_name : t.buyer_name;
                const expiry = getPendingPaymentExpiry(t);
                const paymentHint = uiState.state === 'EXPIRED'
                  ? 'Expired due to no payment'
                  : uiState.state === 'FUNDED'
                    ? expiry.expiresInLabel
                    : null;
                return (
                  <div
                    key={t.transaction_id}
                    role="button" tabIndex={0}
                    onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); goToTransaction(t); } }}
                    data-testid={`transaction-row-${t.transaction_id}`}
                    onClick={() => goToTransaction(t)}
                    className="tt-row-hover"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                      padding: '14px 10px', borderRadius: D.radiusSm, cursor: 'pointer',
                      borderBottom: `1px solid ${D.border}`,
                    }}
                  >
                    <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                      <p style={{ margin: 0, color: D.text, fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.item_description}>
                        {t.item_description}
                      </p>
                      <p style={{ margin: '3px 0 0', color: D.sub, fontSize: 12.5 }}>
                        {counterparty || 'Participant profile pending'} · {t.share_code || 'Reference pending'}
                      </p>
                      <p style={{ margin: '5px 0 0', color: D.sub, fontSize: 12 }}>Next · {journey.owner}: {journey.next}
                      </p>
                      <p style={{ margin: '5px 0 0', color: D.dim, fontSize: 11 }}>{t.updated_at ? 'Updated' : 'Created'} {new Date(t.updated_at || t.created_at).toLocaleDateString('en-ZA')}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 'auto' }}>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ color: D.text, fontWeight: 800, fontSize: 15 }}>{fmt(t.item_price)}</span>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: D.sub }}>Item value</p>
                        {paymentHint && <p style={{ margin: '2px 0 0', fontSize: 11, color: D.sub }}>{paymentHint}</p>}
                      </div>
                      <StatusPill label={uiState.label} color={uiState.color || D.sub} />
                      <ArrowRight size={14} color={D.dim} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}

export default TransactionsList;
